import { ReportBuilderFormData } from '@modules/dynamicReports/ReportForms';
import { MemoExoticComponent, ComponentType } from 'react';
import RGL from 'react-grid-layout';
import { DashboardWidgetData } from './../../dynamicReports/ReportForms';
import { BarChartWidget, defineBarChartWidgetConfirmForm, BarChartWidgetConfig } from './BarChartWidget';
import { LineChartWidget, LineChartWidgetConfig, defineLineChartWidgetConfirmForm } from './LineChartWidget';
import { ListWidget } from './ListWidget';
import { PieChartWidget } from './PieChartWidget';
import { ProgressCircleWidget } from './ProgressCircleWidget';
import { StatExpandedWidget } from './StatExpandedWidget';
import { StatWidget } from './StatWidget';
import { StatWithChartWidget, defineStatWithChartWidgetConfigForm, StatWithChartWidgetConfig } from './StatWithChartWidget';
import { TextWidget } from './TextWidget';
import { TimelineWidget } from './TimelineWidget';
import { SectionTitleWidget, SectionTitleWidgetConfig, defineSectiontWidgetConfirmForm } from './SectionTitleWidget';
import { FunnelWidget, FunnelWidgetConfig, defineFunnelWidgetConfigForm } from './FunnelWidget';
import { FormGroup } from '@core/utils/form';
import { StatChartWidget, StatChartWidgetConfig, defineStatChartWidgetConfigForm } from './StatChartWidget';
import { DashboardInterval } from '@business/entities/dashboard';
import { GraphTypes } from '@business/entities/graph';
import { defineProgressCircleWidgetConfigForm, ProgressCircleWidgetConfig } from './ProgressCircleWidget/ProgressCircleWidgetConfig';

export type WidgetLayout = Omit<RGL.Layout, 'i'>;

export enum WidgetType {
  stat = 'stat',
  statWithChart = 'stat-with-chart',
  timeline = 'timeline',
  text = 'text',
  linechart = 'linechart',
  barChart = 'bar-chart',
  progressCircle = 'progress-circle',
  pieChart = 'pie-chart',
  statExpanded = 'stat-expanded',
  list = 'list',
  sectionTitle = 'sectionTitle',
  funnel = 'funnel',
  graph = 'graph',
}

export interface DashboardWidgetSettings {
  component: MemoExoticComponent<ComponentType<any>>;
  defaultData: DashboardWidgetData;
  configForm: () => FormGroup<any> | undefined;
  configComponent?: MemoExoticComponent<ComponentType<any>>;
}

function getDefaultSettgingsValue(data: Pick<DashboardWidgetData, 'widgetType' | 'title' | 'grid'>): DashboardWidgetData {
  return {
    description: '',
    interval: DashboardInterval.sameAsDashboard,
    total: 0,
    dashboard: undefined,
    ...data,
  };
}

export const WIDGET_COMPONENTS_DATA: Record<WidgetType, DashboardWidgetSettings> = {
  stat: {
    component: StatWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Stat',
      widgetType: WidgetType.stat,
      grid: { x: 4, y: 0, minH: 1, minW: 2, h: 1, w: 4, maxH: 1 },
    }),
    configForm: () => undefined,
  },
  'stat-with-chart': {
    component: StatWithChartWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Stat with Chart',
      widgetType: WidgetType.statWithChart,
      grid: { x: 3, y: 0, minH: 1, minW: 2, h: 1, w: 6 },
    }),
    configForm: defineStatWithChartWidgetConfigForm,
    configComponent: StatWithChartWidgetConfig,
  },
  timeline: {
    component: TimelineWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Timeline',
      widgetType: WidgetType.timeline,
      grid: { x: 3, y: 0, minH: 2, minW: 6, h: 3, w: 6 },
    }),
    configForm: () => undefined,
  },
  text: {
    component: TextWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Text',
      widgetType: WidgetType.text,
      grid: { x: 3, y: 0, minH: 1, minW: 3, h: 2, w: 6 },
    }),
    configForm: () => undefined,
  },
  linechart: {
    component: LineChartWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Line Chart',
      widgetType: WidgetType.linechart,
      grid: { x: 3, y: 0, minH: 3, minW: 4, h: 3, w: 6 },
    }),
    configForm: defineLineChartWidgetConfirmForm,
    configComponent: LineChartWidgetConfig,
  },
  graph: {
    component: StatChartWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Stat Chart',
      widgetType: WidgetType.graph,
      grid: { x: 3, y: 0, minH: 3, minW: 4, h: 3, w: 6 },
    }),
    configForm: defineStatChartWidgetConfigForm,
    configComponent: StatChartWidgetConfig,
  },
  'bar-chart': {
    component: BarChartWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Bar Chart',
      widgetType: WidgetType.barChart,
      grid: { x: 4, y: 0, minH: 3, minW: 4, h: 3, w: 4 },
    }),
    configForm: defineBarChartWidgetConfirmForm,
    configComponent: BarChartWidgetConfig,
  },
  funnel: {
    component: FunnelWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Funnel',
      widgetType: WidgetType.funnel,
      grid: { x: 4, y: 0, minH: 6, minW: 4, h: 6, w: 4 },
    }),
    configForm: defineFunnelWidgetConfigForm,
    configComponent: FunnelWidgetConfig,
  },
  'progress-circle': {
    component: ProgressCircleWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Progress Circle',
      widgetType: WidgetType.progressCircle,
      grid: { x: 4, y: 0, minH: 2, minW: 2, h: 2, w: 2 },
    }),
    configForm: defineProgressCircleWidgetConfigForm,
    configComponent: ProgressCircleWidgetConfig,
  },
  'pie-chart': {
    component: PieChartWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Pie Chart',
      widgetType: WidgetType.pieChart,
      grid: { x: 3, y: 0, minH: 4, minW: 4, h: 4, w: 6 },
    }),
    configForm: () => undefined,
  },
  'stat-expanded': {
    component: StatExpandedWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Stat Expanded',
      widgetType: WidgetType.statExpanded,
      grid: { x: 4, y: 0, minH: 3, minW: 4, h: 3, w: 4 },
    }),
    configForm: () => undefined,
  },
  list: {
    component: ListWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'List',
      widgetType: WidgetType.list,
      grid: { x: 3, y: 0, minH: 2, minW: 4, h: 2, w: 6 },
    }),
    configForm: () => undefined,
  },
  sectionTitle: {
    component: SectionTitleWidget,
    defaultData: getDefaultSettgingsValue({
      title: 'Section Title',
      widgetType: WidgetType.sectionTitle,
      grid: { x: 0, y: 0, minH: 1, minW: 12, h: 1, w: 12 },
    }),
    configForm: defineSectiontWidgetConfirmForm,
    configComponent: SectionTitleWidgetConfig,
  },
};

export interface WidgetProps<R = any, T = any> {
  settings: DashboardWidgetData;
  data: ReportBuilderFormData;
  dateRange?: string;
  result?: R;
  config?: T;
  print?: boolean;
  hasError?: boolean;
  isPreview?: boolean;
  onReloadData?: () => void;
  onUpdateGraphType?: (type: GraphTypes) => void;
}

export interface WidgetConfirmProps<F, D> {
  form: F;
  data: D;
}
